/* eslint max-classes-per-file: 0 */

import { AxiosRequestConfig } from 'axios'
import {
  Publication,
  PublicationCollection,
  PublicationParticipantCollection,
  PublicationStorage,
  PublicationStorageCollection,
  PublicationStorageFile,
  PublicationStorageFileCollection,
  PublicationStorageFileHistory,
  PublicationStorageFileHistoryCollection,
  UsersViewPublicationCollection,
} from '@/typings/api/Publication'
import { User } from '@/typings/api/User'
import { BaseApiParams, BaseApiResponse } from '@/typings/common'

import { BaseApiAuth } from '@/api/BaseApiAuth'

export class PublicationsApi extends BaseApiAuth<
  Publication,
  PublicationCollection
  > {
  protected readonly endpoint = 'publications/publications';

  public archive<R = BaseApiResponse<Publication>>(
    id: Publication['id'],
    params: Record<string, unknown> = {},
    config: AxiosRequestConfig = {},
  ) {
    const path = `${id}/archive/`
    const [url] = this.getUrl(path, params)
    return this.request<never, R>('POST', url, { params, ...config })
  }

  public unarchive<R = BaseApiResponse<Publication>>(
    id: Publication['id'],
    params: Record<string, unknown> = {},
    config: AxiosRequestConfig = {},
  ) {
    const path = `${id}/unarchive/`
    const [url] = this.getUrl(path, params)
    return this.request<never, R>('POST', url, { params, ...config })
  }

  public getDefaultRedirect(
    data: Record<string, unknown> = {},
    config: AxiosRequestConfig = {},
  ) {
    const path = 'redirect/'
    const [url, params] = this.getUrl(path, data)
    return this.request<never, Publication>('GET', url, { params, ...config })
  }

  public redirect(
    id: Publication['id'],
    data: Record<string, unknown> = {},
    config: AxiosRequestConfig = {},
  ) {
    const path = `${id}/redirect/`
    const [url] = this.getUrl(path, data)
    type IResponse = { data: { is_redirect_to_self: boolean } }
    return this.request<never, IResponse>('POST', url, { data, ...config })
  }

  public copy<R = BaseApiResponse<Publication>>(
    id: Publication['id'],
    params: Record<string, unknown> = {},
    config: AxiosRequestConfig = {},
  ) {
    const path = `${id}/copy/`
    const [url] = this.getUrl(path, params)
    return this.request<never, R>('POST', url, { params, ...config })
  }

  public getCuratorParticipants<R = BaseApiResponse<PublicationParticipantCollection>>(id: Publication['id'], params?: Record<string, unknown>, config?: AxiosRequestConfig) {
    const path = `${id}/curators/`
    const [url, params2] = this.getUrl(path, params)
    return this.request<never, R>('GET', url, { params: params2, ...config })
  }

  public addCuratorParticipants(id: Publication['id'], data: Record<string, unknown>, config?: AxiosRequestConfig) {
    const path = `${id}/add-curators/`
    const [url] = this.getUrl(path, data)
    return this.request('POST', url, { data, ...config })
  }

  public removeCuratorParticipants(id: Publication['id'], data: Record<string, unknown>, config?: AxiosRequestConfig) {
    const path = `${id}/remove-curators/`
    const [url] = this.getUrl(path, data)
    return this.request('POST', url, { data, ...config })
  }

  public getParticipants<R = BaseApiResponse<PublicationParticipantCollection>>(id: Publication['id'], params?: Record<string, unknown>, config?: AxiosRequestConfig) {
    const path = `${id}/participants/`
    const [url, params2] = this.getUrl(path, params)
    return this.request<never, R>('GET', url, { params: params2, ...config })
  }

  public addParticipants(id: Publication['id'], data: Record<string, unknown>, config?: AxiosRequestConfig) {
    const path = `${id}/add-participants/`
    const [url] = this.getUrl(path, data)
    return this.request('POST', url, { data, ...config })
  }

  public removeParticipants(id: Publication['id'], data: Record<string, unknown>, config?: AxiosRequestConfig) {
    const path = `${id}/remove-participants/`
    const [url] = this.getUrl(path, data)
    return this.request('POST', url, { data, ...config })
  }

  public getAvailablePublications<R = BaseApiResponse<UsersViewPublicationCollection>>(user_id: User['id'], config?: AxiosRequestConfig) {
    const path = 'user/'
    const data = { user_id }
    const [url, params] = this.getUrl(path, data)
    return this.request<never, R>('GET', url, { params, ...config })
  }
}

interface PublicationApiParams extends BaseApiParams {
  publication_id?: Publication['id'];
}

export class PublicationsStorageApi extends BaseApiAuth<
  PublicationStorage,
  PublicationStorageCollection,
  PublicationApiParams
  > {
  protected readonly endpoint = '/publications/publications/:publication_id/storage';

  public addParticipants(id: PublicationStorage['id'], data: Record<string, unknown>, config?: AxiosRequestConfig) {
    const path = `${id}/add-participants/`
    const [url] = this.getUrl(path, data)
    return this.request('POST', url, { data, ...config })
  }

  public removeParticipants(id: PublicationStorage['id'], data: Record<string, unknown>, config?: AxiosRequestConfig) {
    const path = `${id}/remove-participants/`
    const [url] = this.getUrl(path, data)
    return this.request('POST', url, { data, ...config })
  }
}

interface PublicationsStorageFileApiParams extends PublicationApiParams {
  storage_id?: PublicationStorage['id'];
}

export class PublicationsStorageFilesApi extends BaseApiAuth<
  PublicationStorageFile,
  PublicationStorageFileCollection,
  PublicationsStorageFileApiParams,
  BaseApiResponse<PublicationStorageFile>
  > {
  protected readonly endpoint = '/publications/publications/:publication_id/storage/:storage_id/files';

  public create<
    D extends PublicationsStorageFileApiParams,
    R extends BaseApiResponse<PublicationStorageFile> = BaseApiResponse<PublicationStorageFile>
    >(
    rawData: D,
    config?: AxiosRequestConfig,
  ) {
    const path = 'create/'
    const [url, { unzip, parse, ...data }] = this.getUrl(path, rawData)
    const params = { unzip, parse }

    const formData = new FormData()
    Object.entries(data).forEach(([key, rawValue]) => {
      if (!rawValue) return

      if (Array.isArray(rawValue)) {
        rawValue.forEach((value) => {
          formData.append(key, value)
        })
      } else {
        // @ts-ignore TODO:
        formData.append(key, rawValue)
      }
    })

    const options = { data: formData, params, ...config }
    return this.request<never, R>('POST', url, options)
  }

  public update<
    D extends PublicationsStorageFileApiParams,
    R extends BaseApiResponse<PublicationStorageFile> = BaseApiResponse<PublicationStorageFile>
    >(
    id: string|number,
    rawData: D,
    config?: AxiosRequestConfig,
  ) {
    const path = `${id}/update/`
    const [url, { parse, ...data }] = this.getUrl(path, rawData)
    const params = { parse }

    const formData = new FormData()
    Object.entries(data).forEach(([key, rawValue]) => {
      if (!rawValue) return

      if (Array.isArray(rawValue)) {
        rawValue.forEach((value) => {
          formData.append(key, value)
        })
      } else {
        // @ts-ignore TODO:
        formData.append(key, rawValue)
      }
    })

    const options = { data: formData, params, ...config }
    return this.request<never, R>('POST', url, options)
  }

  public deleteAll(rawParams: Record<string, unknown>, config?: AxiosRequestConfig) {
    const url = 'delete-all/'

    const [path, params] = this.getUrl(url, rawParams)
    const options = { params, ...config }
    return this.request<never, never>('POST', path, options)
  }
}


interface PublicationsStorageFileHistoryApiParams extends PublicationsStorageFileApiParams {
}

export class PublicationsStorageFilesHistoryApi extends BaseApiAuth<
  PublicationStorageFileHistory,
  PublicationStorageFileHistoryCollection,
  PublicationsStorageFileHistoryApiParams,
  BaseApiResponse<PublicationStorageFileHistory>
  > {
  protected readonly endpoint = '/publications/publications/:publication_id/storage/:storage_id/files/history';

  public historyReport(params: Record<string, unknown>, config: AxiosRequestConfig = {}) {
    const path = '/'
    let [url] = this.getUrl(path, params)
    url = url.replace('history', 'history-report')
    return this.request<never>('GET', url, config)
  }
}
